import { AuthServiceV4 } from '@oneaudi/audi-auth-service';
import { TextButton } from '@oneaudi/unified-web-alpha-components';
import { useAppServices, useAppState } from '~/app/contexts/app-context';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { i18nKeys, UseI18n, useI18n } from '~/app/i18n/i18n';
import { AEM_MARKET_CONFIG, useMarketConfig } from '~/app/market-config';
import { useSaveConfigurationNotification } from '~/app/pages/components/notification/use-notification';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { ErrorType, EventAction } from '~/app/tracking/feld-m-types';
import { windowAvailable } from '~/utils/client-detection';

export const SaveConfiguration = () => {
	const { i18n } = useI18n();
	const { market } = useMarketConfig();
	const { isLoggedIn, audiCode } = useAppState();
	const {
		featureServices: { authService },
	} = useAppServices();
	const { closeDialog } = useDialogs();
	const labelText = isLoggedIn
		? i18n('ngc.saveConfiguration.myAudi.save')
		: i18n('ngc.saveConfiguration.myAudi.login');
	const audiCodeString = audiCode?.data.id;
	const { showNotification } = useSaveConfigurationNotification();

	const saveOrLogin = async () => {
		if (audiCodeString) {
			if (!isLoggedIn) {
				const url = new URL(window.document.location.href);
				url.searchParams.set('saveconfig', audiCodeString);

				appTrackingService.summaryClickEvent({
					eventAction: EventAction.ExitLink,
					eventName: `configurator (save configuration) - click on login myAudi`,
					componentName: 'configurator-save-configuration',
					label: labelText,
					elementName: 'button',
				});

				await authService.login(undefined, typeof window !== 'undefined' ? url.toString() : undefined);
			} else {
				appTrackingService.summaryClickEvent({
					eventAction: EventAction.InternalLink,
					eventName: `configurator (save configuration) - click on save to my wishlist`,
					componentName: 'configurator-save-configuration',
					label: labelText,
					elementName: 'button',
				});

				const result = await saveAudicode(audiCodeString, authService, i18n);
				if (market === 'DE') {
					await saveAudicodeUCAS(audiCodeString, authService);
				}
				showNotification((state) => ({ ...state, ...result }));
				closeDialog('audiCode');
			}
		}
	};

	return (
		<TextButton
			data-testid="save-configuration"
			variant="primary"
			isLoading={!audiCode || !audiCodeString}
			onClick={saveOrLogin}
			size="sm"
			id="login">
			{labelText}
		</TextButton>
	);
};

export const saveAudicode = async (audiCode: string | undefined, authService: AuthServiceV4, i18n: UseI18n) => {
	const wishlistGqlUrl = '/graphql';

	const saveToWishListQuery =
		'mutation SaveWishlist($input: SaveWishlistInput!) { saveWishlist(input: $input) { wishlist { id country vehicleCategory vehicleId createdAt } } }';

	const handleError = () => {
		appTrackingService.trackError({
			name: ErrorType.AUDI_CODE,
			userMessage: i18n('ngc.saveConfiguration.generalError.message'),
		});
		return { message: 'ngc.saveConfiguration.generalError.message' as keyof typeof i18nKeys, isError: true };
	};

	try {
		const saveToWishlistResponse = await authService.fetch(wishlistGqlUrl, {
			resourceHostId: 'omnigraph',
			method: 'POST',
			body: JSON.stringify({
				query: saveToWishListQuery,
				variables: {
					input: {
						country: AEM_MARKET_CONFIG.market.toUpperCase(),
						vehicleCategory: 'CONFIG',
						vehicleId: audiCode,
					},
				},
			}),
			headers: {
				'Content-Type': 'application/json',
				'apollographql-client-name': 'ngc-one-pager',
				'apollographql-client-version': '1_29',
			},
		});

		// GraphQL: while the http response might be OK, the body itself may carry errors
		if (saveToWishlistResponse.ok) {
			const response = await saveToWishlistResponse.json();
			const errorCode = response && response.errors ? response.errors[0].extensions.statusCode : undefined;
			if (errorCode) {
				if (errorCode === 409) {
					return {
						message: 'ngc.saveConfiguration.duplicate.message' as keyof typeof i18nKeys,
						isError: true,
					};
				}

				if (errorCode === 410) {
					return {
						message: 'ngc.saveConfiguration.noSpaceError.message' as keyof typeof i18nKeys,
						isError: true,
					};
				}

				console.error('Unexpected error returned:', errorCode);
				return handleError();
			}

			return { message: 'ngc.saveConfiguration.success.message' as keyof typeof i18nKeys, isError: false };
		}

		console.error('Unexpected response status:', saveToWishlistResponse.status);
		return handleError();
	} catch (e) {
		console.error('Error while saving audiCode:', e);
		return handleError();
	}
};

//TODO Remove function once german market does not require UCAS anymore
export const saveAudicodeUCAS = async (audiCode: string | undefined, authService: AuthServiceV4) => {
	const STAGE_PARAMETER = windowAvailable() ? (new URLSearchParams(window?.location.search).get('stage') ?? '') : '';
	const resourceHostId = STAGE_PARAMETER === 'int' ? 'fa-ngc-one-pager-api-dev' : 'fa-ngc-one-pager-api-prod';
	try {
		await authService.fetch(`api/add?audicode=${audiCode}`, {
			resourceHostId,
			method: 'PUT',
		});
	} catch (e) {
		console.error('Error while saving audiCode to UCAS:', e);
	}
};
